// @djsem/render-state — Brand World (Prompt 10A).
//
// Reusable visual IDENTITY (palettes, tokens, style/mood tags, asset refs, rights state)
// applied to visual/lighting scenes, show packs, and previews. Brand World changes
// PRESENTATION STYLE only — it never touches semantic meaning, cue intent, chapter
// structure, track order, live-readiness truth, or safety state (rule 7/9). Lives in
// render-state because it applies to scene contracts (which render-state owns) and must
// stay renderer-neutral: applied hints are abstract NAMES, never renderer/hardware commands.

import {
  vArray, vObject, vString, vUnion, vRefine, type Validator,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "@djsem/shared";
import type { VisualScene, LightingScene } from "./scenes";
import { VISUAL_FORBIDDEN } from "./scenes";

// ---- contracts --------------------------------------------------------------

/** Category of a brand asset reference. */
export type DJSEMBrandAssetType =
  | "logo" | "image" | "texture" | "font_reference" | "color_palette" | "motion_reference" | "style_reference";

/** Approval lifecycle status of a brand asset. */
export type DJSEMBrandAssetStatus = "draft" | "approved" | "restricted" | "rejected";
/** Rights clearance status of a brand asset. */
export type DJSEMBrandRightsStatus = "unknown" | "owned" | "licensed" | "restricted";
/** Semantic role a brand color token plays in a palette. */
export type DJSEMBrandColorRole =
  | "primary" | "secondary" | "accent" | "background" | "foreground" | "warning" | "safe";

/** One named color in a Brand World's palette. */
export interface DJSEMBrandColorToken {
  id: string;
  label: string;
  value: string; // hex
  role: DJSEMBrandColorRole;
}

/** A reference to a brand asset (logo/image/texture/etc.), with rights and approval status. */
export interface DJSEMBrandAssetRef {
  id: string;
  assetType: DJSEMBrandAssetType;
  label: string;
  uri?: string;
  r2Key?: string;
  hash?: string;
  rightsStatus: DJSEMBrandRightsStatus;
  status: DJSEMBrandAssetStatus;
  notes?: string;
}

/**
 * A reusable visual identity (palettes, tokens, style/mood tags, asset refs, rights state)
 * applied to visual/lighting scenes and previews. Changes presentation style only — it
 * never touches semantic meaning, cue intent, chapter structure, or safety state.
 */
export interface DJSEMBrandWorld {
  id: string;
  label: string;
  description?: string;
  colorTokens: DJSEMBrandColorToken[];
  typographyRefs: DJSEMBrandAssetRef[];
  logoRefs: DJSEMBrandAssetRef[];
  imageRefs: DJSEMBrandAssetRef[];
  textureRefs: DJSEMBrandAssetRef[];
  styleTags: string[];
  moodTags: string[];
  restrictedSymbols: string[];
  restrictedColors: string[];
  safetyNotes: string[];
  createdAt: string;
  updatedAt: string;
}

// ---- validators -------------------------------------------------------------

const HEX = /^#[0-9a-fA-F]{6}$/;
const vColorRole: Validator = vUnion(...(["primary", "secondary", "accent", "background", "foreground", "warning", "safe"] as const).map((r) => vString({ enum: [r] })));
/** Validator for {@link DJSEMBrandColorToken}. */
export const vBrandColorToken: Validator = vObject({
  id: vString({ min: 1 }), label: vString({ min: 1 }), value: vString({ pattern: HEX }), role: vColorRole,
});
const vAssetStatus: Validator = vUnion(...(["draft", "approved", "restricted", "rejected"] as const).map((s) => vString({ enum: [s] })));
const vRights: Validator = vUnion(...(["unknown", "owned", "licensed", "restricted"] as const).map((s) => vString({ enum: [s] })));
const vAssetType: Validator = vUnion(...(["logo", "image", "texture", "font_reference", "color_palette", "motion_reference", "style_reference"] as const).map((s) => vString({ enum: [s] })));
/** Validator for {@link DJSEMBrandAssetRef}. */
export const vBrandAssetRef: Validator = vObject(
  { id: vString({ min: 1 }), assetType: vAssetType, label: vString({ min: 1 }), rightsStatus: vRights, status: vAssetStatus },
  { uri: vString({ min: 1 }), r2Key: vString({ min: 1 }), hash: vString({ min: 1 }), notes: vString({}) },
);

/** Validator for {@link DJSEMBrandWorld}. */
export const vBrandWorld: Validator<DJSEMBrandWorld> = vObject(
  {
    id: vString({ min: 1 }), label: vString({ min: 1 }),
    colorTokens: vArray(vBrandColorToken), typographyRefs: vArray(vBrandAssetRef),
    logoRefs: vArray(vBrandAssetRef), imageRefs: vArray(vBrandAssetRef), textureRefs: vArray(vBrandAssetRef),
    styleTags: vArray(vString({ min: 1 })), moodTags: vArray(vString({ min: 1 })),
    restrictedSymbols: vArray(vString({})), restrictedColors: vArray(vString({})), safetyNotes: vArray(vString({})),
    createdAt: vString({ min: 1 }), updatedAt: vString({ min: 1 }),
  },
  { description: vString({}) },
) as Validator<DJSEMBrandWorld>;

// ---- inputs -----------------------------------------------------------------

/** Input for {@link BrandWorldService.createBrandWorld}. */
export interface CreateBrandWorldInput {
  id?: string; label: string; description?: string;
  colorTokens?: DJSEMBrandColorToken[]; styleTags?: string[]; moodTags?: string[];
  restrictedSymbols?: string[]; restrictedColors?: string[]; createdAt?: string;
}
/** Input for {@link BrandWorldService.updateBrandWorld}. */
export interface UpdateBrandWorldInput { brandWorld: DJSEMBrandWorld; patch: Partial<Omit<DJSEMBrandWorld, "id" | "createdAt">>; updatedAt?: string; }
/** Input for {@link BrandWorldService.approveAsset}. */
export interface ApproveBrandAssetInput { brandWorld: DJSEMBrandWorld; assetId: string; }
/** Input for {@link BrandWorldService.restrictAsset}. */
export interface RestrictBrandAssetInput { brandWorld: DJSEMBrandWorld; assetId: string; note?: string; }
/** Input for {@link BrandWorldService.applyBrandToVisualScene}. */
export interface ApplyBrandToVisualSceneInput { brandWorld: DJSEMBrandWorld; scene: VisualScene; styleHintNames?: string[]; }
/** Input for {@link BrandWorldService.applyBrandToLightingScene}. */
export interface ApplyBrandToLightingSceneInput { brandWorld: DJSEMBrandWorld; scene: LightingScene; }

// ---- service (class-based; hard rule 12) ------------------------------------

let _bw = 0;
/** Manages Brand World identities: creation, updates, asset approval, and application to scenes. */
export class BrandWorldService {
  /**
   * Creates a new Brand World.
   * @param input - label/description, seed color tokens, style/mood tags, restrictions.
   * @returns the new Brand World with deduplicated tags/restrictions and empty asset lists.
   */
  createBrandWorld(input: CreateBrandWorldInput): DJSEMBrandWorld {
    const now = input.createdAt ?? new Date().toISOString();
    return {
      id: input.id ?? `brand_${++_bw}`, label: input.label, description: input.description,
      colorTokens: input.colorTokens ?? [], typographyRefs: [], logoRefs: [], imageRefs: [], textureRefs: [],
      styleTags: dedupe(input.styleTags ?? []), moodTags: dedupe(input.moodTags ?? []),
      restrictedSymbols: dedupe(input.restrictedSymbols ?? []), restrictedColors: dedupe(input.restrictedColors ?? []),
      safetyNotes: [], createdAt: now, updatedAt: now,
    };
  }

  /**
   * Applies a partial patch to a Brand World. `id` and `createdAt` are never touched.
   * @param input - the Brand World, the patch to apply, and an optional updatedAt override.
   * @returns the patched Brand World.
   */
  updateBrandWorld(input: UpdateBrandWorldInput): DJSEMBrandWorld {
    // never touches id/createdAt
    return { ...input.brandWorld, ...input.patch, id: input.brandWorld.id, createdAt: input.brandWorld.createdAt, updatedAt: input.updatedAt ?? new Date().toISOString() };
  }

  /**
   * Marks a brand asset approved.
   * @param input - the Brand World and the asset id to approve.
   * @returns the updated Brand World.
   */
  approveAsset(input: ApproveBrandAssetInput): DJSEMBrandWorld {
    return this.mapAsset(input.brandWorld, input.assetId, (a) => ({ ...a, status: "approved" }));
  }
  /**
   * Marks a brand asset restricted, optionally attaching a note.
   * @param input - the Brand World, asset id, and an optional restriction note.
   * @returns the updated Brand World.
   */
  restrictAsset(input: RestrictBrandAssetInput): DJSEMBrandWorld {
    return this.mapAsset(input.brandWorld, input.assetId, (a) => ({ ...a, status: "restricted", notes: input.note ?? a.notes }));
  }

  /**
   * Applies brand STYLE to a visual scene, returning a new scene. Semantic and safety
   * fields are copied verbatim; applied hints are abstract style-tag names only, and
   * anything matching renderer/hardware forbidden vocabulary is dropped.
   * @param input - the Brand World, the scene to brand, and optional extra style hint names.
   * @returns a new visual scene with brand presentation applied.
   */
  applyBrandToVisualScene(input: ApplyBrandToVisualSceneInput): VisualScene {
    const { brandWorld, scene } = input;
    const primary = brandWorld.colorTokens.find((t) => t.role === "primary");
    // restricted colors must never be applied
    const paletteAllowed = primary && !brandWorld.restrictedColors.includes(primary.value);
    const hintCandidates = dedupe([...(input.styleHintNames ?? []), ...brandWorld.styleTags, ...brandWorld.moodTags])
      .filter((h) => !VISUAL_FORBIDDEN.test(h)) // never introduce renderer/hardware vocabulary
      .filter((h) => !brandWorld.restrictedSymbols.includes(h)); // restricted symbols blocked
    return {
      ...scene,
      // presentation only:
      brandPaletteId: paletteAllowed ? brandWorld.id : scene.brandPaletteId,
      // Restricted symbols are stripped from the WHOLE merged list — including any that
      // were already on the scene — so a branded scene can never surface a blocked symbol.
      rendererHintsAllowed: dedupe([...scene.rendererHintsAllowed, ...hintCandidates])
        .filter((h) => !brandWorld.restrictedSymbols.includes(h)),
      // semantic + safety copied verbatim (explicit to make the invariant obvious):
      semanticUses: scene.semanticUses, chapterUses: scene.chapterUses,
      visualIntent: scene.visualIntent, safetyLevel: scene.safetyLevel,
    };
  }

  /**
   * Attaches a Brand World pointer to a lighting scene. Fixture states and safety are
   * untouched — the attachment is presentation-only.
   * @param input - the Brand World and the lighting scene to brand.
   * @returns a new lighting scene with the brand palette pointer set.
   */
  applyBrandToLightingScene(input: ApplyBrandToLightingSceneInput): LightingScene {
    const { brandWorld, scene } = input;
    // Lighting brand attachment is a pointer only — fixture states + safety are untouched.
    return {
      ...scene,
      brandPaletteId: brandWorld.id,
      // untouched (explicit):
      semanticUses: scene.semanticUses, chapterUses: scene.chapterUses,
      fixtureGroupStates: scene.fixtureGroupStates, safetyLevel: scene.safetyLevel,
    };
  }

  /**
   * Determines whether a brand asset may be applied. Restricted/rejected assets are never
   * applyable; unknown/restricted rights produce a warning (surfaced to the UI);
   * owned/licensed assets are clean.
   * @param asset - the brand asset to check.
   * @returns whether it's applyable, plus any rights warnings.
   */
  assetApplicability(asset: DJSEMBrandAssetRef): { applyable: boolean; warnings: string[] } {
    const warnings: string[] = [];
    if (asset.status === "restricted" || asset.status === "rejected") return { applyable: false, warnings: [`Asset ${asset.label} is ${asset.status}.`] };
    if (asset.rightsStatus === "unknown") warnings.push(`Rights unknown for ${asset.label}; confirm before public use.`);
    if (asset.rightsStatus === "restricted") warnings.push(`Rights restricted for ${asset.label}.`);
    return { applyable: true, warnings };
  }

  /**
   * Validates a Brand World's structure plus the extra invariant that restricted colors
   * must not also appear as active color tokens.
   * @param brandWorld - the candidate Brand World, of unknown shape.
   * @returns a validation result listing any structural or restricted-color conflicts.
   */
  validateBrandWorld(brandWorld: unknown): DJSEMValidationResult {
    const base: ValidationResult = vBrandWorld(brandWorld);
    const issues = [...base.issues];
    const bw = brandWorld as DJSEMBrandWorld;
    // extra: restricted colors that also appear as color tokens are a conflict to warn on
    if (bw && Array.isArray(bw.colorTokens)) {
      bw.colorTokens.forEach((t, i) => {
        if (bw.restrictedColors?.includes(t.value)) issues.push({ path: `$.colorTokens[${i}]`, message: `color token ${t.value} is also restricted` });
      });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "brand_world");
  }

  // ---- private --------------------------------------------------------------
  private mapAsset(bw: DJSEMBrandWorld, assetId: string, fn: (a: DJSEMBrandAssetRef) => DJSEMBrandAssetRef): DJSEMBrandWorld {
    const map = (arr: DJSEMBrandAssetRef[]) => arr.map((a) => (a.id === assetId ? fn(a) : a));
    return {
      ...bw,
      typographyRefs: map(bw.typographyRefs), logoRefs: map(bw.logoRefs),
      imageRefs: map(bw.imageRefs), textureRefs: map(bw.textureRefs),
      updatedAt: bw.updatedAt,
    };
  }
}

function dedupe<T>(xs: T[]): T[] { return [...new Set(xs)]; }
